import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'react-bootstrap';
import { uploadDocumentForm } from './documentUpload';
import { validateImageBeforeUpload } from './imageValidation';

/**
 * Reusable document upload button with integrated states.
 * label and accept are dynamic — pass per use case (e.g. CSV, PDF, images).
 * - Default: buttonLabel
 * - Uploading: "Uploading..."
 * - Success: uploadedLabel, click to replace
 */
const DocumentUploadButton = ({
  value,
  onUpload,
  onError,
  onUploadingChange,
  accept,
  uploadOptions = {},
  label,
  buttonLabel,
  uploadedLabel = '✓ Uploaded — click to change',
  required = false,
  buttonSize = 'sm',
  disabled = false,
  className = '',
  validationKey,
  dimensionRequirement,
}) => {
  const resolvedLabel = label ?? '';
  const resolvedButtonLabel =
  resolvedLabel === '' ? (
    <>
      {buttonLabel} &nbsp; {required && <span className="text-danger">*</span>}
    </>
  ) : (
    <>
      {resolvedLabel} &nbsp; {required && <span className="text-danger">*</span>}
    </>
  );

  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setIsUploading(true);
    onUploadingChange?.(true);
    try {
      if (validationKey) {
        await validateImageBeforeUpload(file, validationKey);
      }
      const url = await uploadDocumentForm(file, uploadOptions);
      onUpload?.(url);
      setError('');
    } catch (err) {
      const msg = err?.message || 'Upload failed';
      setError(msg);
      onError?.(err);
    } finally {
      setIsUploading(false);
      onUploadingChange?.(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const hasValue = Boolean(value?.trim());
  const handleRemove = () => {
    setError('');
    onUpload?.('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className={className}>
      {resolvedLabel && (
        <label className="d-block mb-2">
          {resolvedLabel}
          {required && <span className="text-danger"> *</span>}
        </label>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileChange}
        disabled={isUploading || disabled}
        style={{ display: 'none' }}
        aria-label={resolvedLabel}
      />
      <div className="d-flex align-items-center flex-wrap gap-2">
        <Button
          type="button"
          variant={hasValue ? 'success' : 'outline-primary'}
          size={buttonSize}
          disabled={isUploading || disabled}
          onClick={() => fileInputRef.current?.click()}
        >
          {isUploading
            ? 'Uploading...'
            : hasValue
            ? uploadedLabel
            : resolvedButtonLabel}
        </Button>

        {hasValue && (
          <Button
            type="button"
            size={buttonSize}
            disabled={isUploading || disabled}
            className="btn-danger-fill"
            onClick={handleRemove}
          >
            Remove
          </Button>
        )}
      </div>
      {dimensionRequirement && (
        <div className="text-muted small mt-1">{dimensionRequirement}</div>
      )}
      {error && <div className="text-danger small mt-1">{error}</div>}
    </div>
  );
};

DocumentUploadButton.propTypes = {
  value: PropTypes.string,
  onUpload: PropTypes.func.isRequired,
  onError: PropTypes.func,
  onUploadingChange: PropTypes.func,
  /** File input accept attribute, e.g. ".csv,text/csv", ".pdf", "image/*" */
  accept: PropTypes.string,
  uploadOptions: PropTypes.shape({
    service_name: PropTypes.string,
    doc_type: PropTypes.string,
    doc_name: PropTypes.string,
    is_public: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    is_view_only: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  }),
  /** Label above the button (dynamic per use case) */
  label: PropTypes.string,
  /** Button text when no file uploaded (defaults to label) */
  buttonLabel: PropTypes.string,
  /** Button text after successful upload */
  uploadedLabel: PropTypes.string,
  required: PropTypes.bool,
  buttonSize: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  /** Frontend validation profile key (dimension rules checked before upload). */
  validationKey: PropTypes.string,
  /** Helper text shown under the upload button (e.g. required dimensions). */
  dimensionRequirement: PropTypes.string,
};

DocumentUploadButton.defaultProps = {
  value: '',
  onError: undefined,
  onUploadingChange: undefined,
  accept: undefined,
  uploadOptions: {},
  label: undefined,
  buttonLabel: undefined,
  uploadedLabel: '✓ Uploaded — click to change',
  required: false,
  buttonSize: 'sm',
  disabled: false,
  className: '',
  validationKey: undefined,
  dimensionRequirement: undefined,
};

export default DocumentUploadButton;
